// @flow
import { isPlainObject } from "../../utils/helpers/objects";

import BaseModel from "../base/BaseModel";
import ConstraintCollection from "./input-constraints/ConstraintCollection";
import ErrorCollection from "../error/ErrorCollection";
import LinkCollection from "../links/LinkCollection";

import StringLengthConstraint from "./input-constraints/StringLengthConstraint";
import MandatoryConstraint from "./input-constraints/MandatoryConstraint";

import ConceptDetailModel from "../concepts/ConceptDetailModel";

import { DISABLED, MANDATORY } from "../../constants/LayoutHints";
import {
  ATTRIBUTE_WIDTH,
  TIMEVERSION_FILTER_NAME,
} from "../../constants/Constants";

import { getSetting, hasAllContentInData } from "../../constants/Settings";

import {
  IllegalArgumentException,
  UnsupportedOperationException,
} from "../../exceptions";

import type { MessageParameters } from "../../i18n/types";
import type {
  AttributeType,
  FormErrorAnchor,
  IModelWithChildModels,
  ModelOptions,
  ModularUIModel,
} from "../types";
import type LinkModel from "../links/LinkModel";
import type ContentConfiguration from "../contentconfiguration/ContentConfiguration";
import type ContentConfigurationElements from "../contentconfiguration/ContentConfigurationElements";
import type LayoutHintRuleCollection from "./layouthint-rules/LayoutHintRuleCollection";
import type AttributeCollection from "./AttributeCollection";
import AttributeContent from "./AttributeContent";
import type ErrorResponse from "../error/ErrorResponse";

/**
 * Attribute model, base model for all kind of attributes
 */
export default class AttributeModel
  extends BaseModel
  implements IModelWithChildModels
{
  _label: string;
  _lastModification: number = 0;

  _initvalue: any;
  _inputvalue: string;
  _value: any;

  _errorCollection: ErrorCollection;
  _serverConstraints: ConstraintCollection = new ConstraintCollection();
  _useClientsideValidation: boolean = getSetting("USE_CLIENTSIDE_VALIDATION");
  _isValid: boolean = true;
  _validatedValue: string | null;
  _isEditable: boolean = false;
  _concept: ConceptDetailModel | null = null;
  _referenceDate: ISO_DATE;
  _isResult: boolean;
  _isHidden: boolean = false;
  _readonly: boolean;
  _disabled: boolean = false;
  _mandatory: boolean;
  _links: LinkCollection;
  _parentKey: ?string;
  _hasContentConfiguration: boolean;
  _layoutHintRules: LayoutHintRuleCollection;

  /**
   */
  constructor(
    attribute: Object,
    attributeContributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(attribute, attributeContributions, modelOptions);

    if (this.hasContentFromData && this.content.label) {
      this._label = this.content.label;
    } else {
      this._label = this.contributions.label;
    }

    this._initvalue = this.data.value;
    this._inputvalue = this.getInitialInputValue(this.data.value);
    this._value = this.data.value;
    this._disabled = this.layouthint.has(DISABLED);

    this._errorCollection = new ErrorCollection("attribute");
    if (attribute && attribute.message) {
      this._errorCollection.addServerError(
        attribute.message.id,
        attribute.message.message,
        attribute.message.parameters,
        attribute.message.layouthint,
      );
    }

    this._referenceDate = this.getData(
      "referenceDate",
      new Date().toISOString().split("T")[0],
    );

    this._isResult = this.getData("isResult", false);

    this._mandatory =
      this.layouthint.has(MANDATORY) || this.contributions.mandatory;

    this._links = new LinkCollection(
      this.data._links,
      this.contributions._links,
      modelOptions,
    );
  }

  /**
   */
  static isApplicableModel(contributions: Object): boolean {
    if (!isPlainObject(contributions)) {
      throw new IllegalArgumentException(
        "Given argument for isApplicableModel is not a contributions object",
      );
    }

    return false;
  }

  /**
   * Retrieve initial input value
   */
  getInitialInputValue(value: any): any {
    return value;
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    if (
      !this.hasContentFromData &&
      this.hasContentConfiguration &&
      this.conceptLink
    ) {
      return [this.conceptLink];
    }
    return [];
  }

  /**
   */
  setChildModels(
    models: Array<ModularUIModel>,
    errors: Array<ErrorResponse>, // eslint-disable-line no-unused-vars
  ): void {
    const conceptHref = this.conceptLink?.href;
    if (conceptHref) {
      this.concept = models.find((model) =>
        model.selfhref.equalsWithParameters(conceptHref),
      );
    }
  }

  /**
   * Getting the attribute key
   */
  get key(): string {
    return this.data.key;
  }

  /**
   */
  get parentKey(): string {
    return this._parentKey || "";
  }

  /**
   */
  set parentKey(parentKey: string) {
    this._parentKey = parentKey;
  }

  /**
   * Getting the attribute name
   */
  get name(): string {
    return this.key;
  }

  /**
   * Getting the type of the attribute
   */
  get type(): string {
    return this.contributions.type;
  }

  /**
   * Getting the label of the attribute
   */
  get label(): string {
    return this._label || "";
  }

  /**
   * Set the label of this attribute
   */
  set label(label: string) {
    this._label = label;
  }

  /**
   */
  set hasContentConfiguration(hasContentConfiguration: boolean) {
    this._hasContentConfiguration = hasContentConfiguration;
  }

  /**
   */
  get hasContentConfiguration(): boolean {
    return this._hasContentConfiguration || false;
  }

  /**
   */
  indicateContentConfiguration(
    contentConfiguration: ContentConfiguration,
  ): void {
    if (contentConfiguration) {
      const hasQuestionConfig = contentConfiguration.questions?.hasConfig();

      const hasEndResultConfig =
        contentConfiguration.isConfiguredEndResultAttribute(this.key);
      const hasIntermediateResultConfig =
        contentConfiguration.isConfiguredIntermediateResultAttribute(this.key);

      this.hasContentConfiguration =
        hasQuestionConfig || hasEndResultConfig || hasIntermediateResultConfig;
    }
  }

  /**
   * Retrieve the first permitted label to render when a concept and contentConfiguration is available
   * Be aware that permission could be in place for labels from a concept.
   */
  getContentConfiguredLabel(
    contentConfiguration: ?ContentConfigurationElements,
  ): string {
    if (this.hasContentFromData) {
      return this.content.label ?? this.label;
    }

    const configuredLabelProperties =
      contentConfiguration && contentConfiguration.labelConfig.length > 0
        ? contentConfiguration.labelConfig[0].types
        : [];

    if (this.concept && configuredLabelProperties.length > 0) {
      const configuredLabels = this.concept
        .getLabelElementByIds(configuredLabelProperties)
        .filter(
          (configuredLabel) =>
            configuredLabel.value && configuredLabel.value !== "",
        );

      if (configuredLabels.length > 0) {
        const [firstConfiguredLabel] = configuredLabelProperties
          .filter((configuredLabelProperty) =>
            configuredLabels.some(
              (configuredLabel) =>
                configuredLabel._id === configuredLabelProperty,
            ),
          )
          .map((configuredLabelProperty) =>
            configuredLabels.find(
              (configuredLabel) =>
                configuredLabel._id === configuredLabelProperty,
            ),
          );

        if (firstConfiguredLabel) {
          return firstConfiguredLabel.value;
        }
      }
    }

    return this.label;
  }

  /**
   */
  get defaultAlignment(): "left" | "center" | "right" {
    return "left";
  }

  /**
   * Handles layout hint align-left, align-center and align-right
   */
  get alignment(): "left" | "center" | "right" {
    const alignmentHints = this.layouthint.getByLayoutHint("align-");

    // return alignment based on layouthint or the default alignment for the attribute
    const alignment = alignmentHints
      ? alignmentHints.substring("align-".length)
      : this.defaultAlignment;

    if (
      alignment === "left" ||
      alignment === "center" ||
      alignment === "right"
    ) {
      return alignment;
    }

    return this.defaultAlignment;
  }

  /**
   * Retrieve links of attribute
   */
  get links(): LinkCollection {
    if (!this._links) {
      this._links = new LinkCollection(
        this.data._links,
        this.contributions._links,
      );
    }

    return this._links;
  }

  /**
   * Retrieve link of attribute when available
   */
  get downloadLink(): ?LinkModel {
    const downloadLink = this.links.getLinksByGroup("download").first;

    if (downloadLink && downloadLink.key === this.key) {
      return downloadLink;
    }

    return null;
  }

  /**
   * Retrieve concept link of attribute when available
   */
  get conceptLink(): ?LinkModel {
    const conceptLink = this.links.getLinkByKey("concept");

    if (conceptLink !== null) {
      conceptLink.href = conceptLink.href.addParameter(
        TIMEVERSION_FILTER_NAME,
        this.referenceDate,
      );

      conceptLink.isCacheable = true;
    }

    return conceptLink;
  }

  /**
   * Get concept information
   */
  get concept(): ConceptDetailModel | null {
    return this._concept ?? null;
  }

  /**
   * Set the concept
   */
  set concept(concept: ?ModularUIModel) {
    this._concept = concept instanceof ConceptDetailModel ? concept : null;
  }

  /**
   * Retrieve reference date of attribute which can be used as entryDate for content
   * to get information in correct time version
   */
  get referenceDate(): ISO_DATE {
    return this._referenceDate;
  }

  /**
   * Set reference date for concepts and content
   */
  set referenceDate(date: ISO_DATE) {
    this._referenceDate = date;
  }

  /**
   * Initial value received from the modular ui service
   */
  get initvalue(): any {
    return this._initvalue;
  }

  /**
   * Retrieve input value
   */
  getInputValue(): string {
    const inputvalue = this._inputvalue;

    return inputvalue ? inputvalue.toString() : "";
  }

  /**
   * Returns the value as entered by the user. This can differ from the internal iso value that is stored
   */
  get inputvalue(): string {
    return this.getInputValue();
  }

  /**
   * Value that is used when retrieving an error collection of this attribute
   * Mostly this is the input value, but sometimes a different value is needed,
   * for example iban and postcode need to validate length without spaces
   */
  get validateValue(): string {
    return this.inputvalue;
  }

  /**
   * Sets the input value to the value entered by the user
   */
  set inputvalue(value: string) {
    this._inputvalue = value;
    this.value = value;

    this.validate(value);
  }

  /**
   */
  hasValue(): boolean {
    return this._value != null && this._value !== "";
  }

  /**
   * Getting the value of the attribute
   */
  getValue(): any {
    return this.hasValue() ? this._value : null;
  }

  /**
   * Getting data ready to be send to the modular ui
   * Returns null when the attribute should not be send to the server
   */
  getFormData(): { [string]: any } | null {
    if (this.inError()) {
      return null;
    }

    return {
      [this.name]: this.value,
    };
  }

  /**
   */
  get formdata(): { [string]: any } | null {
    return this.getFormData();
  }

  /**
   */
  getEmptyFormData(): { [string]: null } {
    return { [this.name]: null };
  }

  /**
   * Getting the value of the attribute
   */
  get value(): any {
    return this.getValue();
  }

  /**
   * Setting a value in the element
   */
  set value(value: ?string) {
    this.updateLastModification();

    this._value = value;
  }

  /**
   */
  formatValue(value: string): string {
    return value;
  }

  /**
   * Getting the readonly value, iso value converted for human reading
   */
  get readonlyvalue(): string {
    return this.value ? this.value.toString() : "";
  }

  /**
   * Getting mandatory status of attribute
   */
  get mandatory(): boolean {
    return this._mandatory || false;
  }

  /**
   */
  get configuredMandatory(): ?boolean {
    return this.layouthint.has(MANDATORY) || this.contributions.mandatory;
  }

  /**
   * Set mandatory status of attribute
   */
  set mandatory(mandatory: boolean) {
    if (mandatory !== this._mandatory) {
      this._validatedValue = null;
    }
    this._mandatory = mandatory;
  }

  /**
   */
  get readonly(): boolean {
    return (
      this._readonly ||
      this.contributions.readonly === true ||
      this.data.static === true ||
      this.isResult === true
    );
  }

  /**
   */
  set readonly(readonly: boolean) {
    this._readonly = readonly;
  }

  /**
   */
  get disabled(): boolean {
    return this._disabled;
  }

  /**
   */
  set disabled(disabled: boolean) {
    this._disabled = disabled;
  }

  /**
   */
  get isResult(): boolean {
    return this._isResult;
  }

  /**
   */
  set isResult(isResult: boolean) {
    this._isResult = isResult;
  }

  /**
   * Getting the display and input format of a attribute
   */
  get format(): string | null {
    return this.getContribution("format");
  }

  /**
   */
  get formatLabel(): string {
    return this.format || "";
  }

  /**
   * Get minimum string length
   */
  get minLength(): number | null {
    return this.getContribution("minLength");
  }

  /**
   * Get maximum string length
   */
  get maxLength(): number | null {
    return this.getContribution("maxLength");
  }

  /**
   */
  get operator(): string {
    return this.getContribution("operator", "");
  }

  /**
   * Get assistant message
   */
  get assistantMessage(): string | null {
    return this.getContribution("assistant");
  }

  /**
   * Get valid status
   */
  get isValid(): boolean {
    return this.validate(this.validateValue);
  }

  /**
   * Get valid status
   */
  getIsValid(): boolean {
    return this._isValid;
  }

  /**
   * Set valid status
   */
  setIsValid(isValid: boolean) {
    this._isValid = isValid;
  }

  /**
   * Retrieve applicable constraint for this attribute
   */
  get constraintCollection(): ConstraintCollection {
    const constraints = new ConstraintCollection();

    constraints.add(this._serverConstraints);

    // Mandatory constraint
    if (this.mandatory) {
      constraints.add(new MandatoryConstraint());
    }

    if (this.minLength || this.maxLength) {
      constraints.add(
        new StringLengthConstraint(this.minLength, this.maxLength),
      );
    }

    constraints.add(this.addConstraints());

    return constraints;
  }

  /**
   * Template method for class extending this model to add extra constraints
   * @abstract
   */
  addConstraints(): ConstraintCollection {
    return new ConstraintCollection();
  }

  /**
   * Used to programmatically overwrite the setting USE_CLIENTSIDE_VALIDATION
   */
  setUseClientsideValidation(enabled: boolean): void {
    this._useClientsideValidation = enabled;
  }

  /**
   * Indicates if client side validation is enabled
   * @returns {boolean}
   */
  useClientsideValidation(): boolean {
    return this._useClientsideValidation ?? true;
  }

  /**
   * Validate input on client side constraint
   */
  validate(value: string): boolean {
    // when client side validation is disabled, this attribute is always valid
    if (!this.useClientsideValidation()) {
      return true;
    }

    if (this.isOptionalAndEmpty(value)) {
      this._isValid = true;
    } else if (this._validatedValue !== value) {
      this._isValid = this.constraintCollection.validate(value);
    }
    this._validatedValue = value;

    return this._isValid;
  }

  /**
   * Indicates if attribute is optional and empty
   */
  isOptionalAndEmpty(value: string): boolean {
    if (value === undefined) {
      throw new IllegalArgumentException(
        "isOptionalAndEmpty method needs value argument",
      );
    }

    return !this.mandatory && value === "";
  }

  /**
   * Retrieve error messages of this attribute
   */
  get errorCollection(): ErrorCollection {
    const collection = new ErrorCollection("attribute", this._errorCollection);

    collection.addConstraints(
      this.constraintCollection.invalidConstraints(this.validateValue),
    );

    return collection;
  }

  /**
   * Check if attribute is in error
   */
  inError(): boolean {
    return !this._isValid && this.isChangedSince(0);
  }

  /**
   * Reset static error messages on attribute
   */
  resetErrors(): void {
    this._errorCollection = new ErrorCollection("attribute");
  }

  /**
   * Registers an error that was received from a server response
   */
  addServerError(error: FormErrorAnchor): void {
    this._errorCollection.addServerError(
      error.id,
      error.message,
      error.properties,
      error.layouthint,
    );
  }

  /**
   */
  removeServerError(id: string): void {
    this._errorCollection.removeServerError(id);
  }

  /**
   */
  hasServerErrors(): boolean {
    return this.errorCollection.serverErrors.length > 0;
  }

  /**
   */
  hasErrors(): boolean {
    return this.errorCollection.hasItems;
  }

  /**
   * Registers a missing error that was received from the server
   */
  // eslint-disable-next-line no-unused-vars
  addMissingError(receivedAttribute?: AttributeType): void {
    this._errorCollection.addServerError("Constraint.Missing");
  }

  /**
   */
  removeMissingError(): void {
    this.removeServerError("Constraint.Missing");
  }

  /**
   */
  addServerConstraint(
    id: string,
    defaultMessage?: string,
    parameters?: MessageParameters,
  ) {
    this._serverConstraints.addConstraint(
      id,
      null,
      defaultMessage,
      parameters,
      this.useClientsideValidation(),
    );
  }

  /**
   */
  formatParameters(parameters: Object): { [string]: string | number } {
    if (parameters == null) {
      return {};
    }

    const params: { [name: string]: string | number } = {};

    Object.keys(parameters).forEach((paramKey: string) => {
      const paramValue = parameters[paramKey];
      if (paramKey.endsWith("-number") || paramKey.endsWith("-date")) {
        params[paramKey] = this.formatValue(paramValue);
      } else {
        params[paramKey] = paramValue;
      }
    });

    return params;
  }

  /**
   */
  get hasDynamicValidationData(): boolean {
    return true;
  }

  /**
   */
  updateValidations(errors: Array<Object>): void {
    if (
      errors.some((error) => error.id === "Constraint.Mandatory") ||
      this.layouthint.has(MANDATORY)
    ) {
      this.mandatory = true;
    } else if (!this.hasValue()) {
      this.mandatory = false;
    }

    this._serverConstraints = new ConstraintCollection();
    this.resetErrors();

    errors
      .filter(
        (error) => error.id !== "Constraint.Mandatory" || !this.hasValue(),
      )
      .forEach((error) => {
        if (this.hasValue()) {
          this._errorCollection.addServerError(
            error.id,
            error.message,
            this.formatParameters(error.properties),
            error.layouthint,
          );
        } else {
          this.addServerConstraint(
            error.id,
            error.message,
            this.formatParameters(error.properties),
          );
        }
      });
  }

  /**
   * Set last modification to current timestamp
   */
  updateLastModification(): void {
    this._lastModification = Date.now();
  }

  /**
   * Inidicates if attribute is changed since a given timestamp (Date.now)
   */
  isChangedSince(timestamp: number): boolean {
    return this._lastModification > timestamp;
  }

  /**
   * Abstract reset method which should be implemented on each attribute that has this attribute as a base class.
   *
   * @abstract
   */
  reset(): void {
    throw new UnsupportedOperationException(
      `Reset method not implemented for ${this.type}`,
    );
  }

  /**
   * Merge old attribute with the attribute from the server.
   * Readonly attributes always need to show the value from the server
   */
  mergeAttribute(oldAttribute: AttributeType): void {
    this.concept = oldAttribute.concept;

    if (
      !oldAttribute.readonly &&
      oldAttribute.isValid &&
      oldAttribute.inputvalue !== null
    ) {
      this.update(oldAttribute.inputvalue);
    }
  }

  /**
   * Abstract update method which should be implemented on each attribute that has this attribute as a base class.
   * @abstract
   */
  // eslint-disable-next-line no-unused-vars
  update(value: string, changedAttribute?: AttributeType): AttributeType {
    throw new UnsupportedOperationException(
      `Update method not implemented for ${this.type}`,
    );
  }

  /**
   * Abstract updateByAttribute method which should be implemented on each attribute that has this attribute as a base class.
   * @abstract
   */
  // eslint-disable-next-line no-unused-vars
  updateByAttribute(otherAttribute: AttributeType): void {
    console.error(
      "update of " +
        this.type +
        " with " +
        otherAttribute.type +
        " not supported",
    );
  }

  /**
   * Indicate if attribute is editable
   */
  set isEditable(isEditable: boolean) {
    this._isEditable = isEditable;
  }

  /**
   * Retrieve if attribute is editable
   */
  get isEditable(): boolean {
    return this._isEditable;
  }

  /**
   * Indicates if the attribute is visible
   */
  get isVisible(): boolean {
    return !this._isHidden;
  }

  /**
   */
  get isHidden(): boolean {
    return this._isHidden;
  }

  /**
   */
  set isHidden(isHidden: boolean) {
    this._isHidden = isHidden;
  }

  /**
   * Explicitly toggle visibility of this attribute
   */
  show(): void {
    this.isHidden = false;
  }

  /**
   */
  hide(): void {
    this.isHidden = true;
  }

  /**
   */
  equals(otherAttribute: AttributeType): boolean {
    return (
      this.key === otherAttribute.key &&
      this.parentKey === otherAttribute.parentKey
    );
  }

  /**
   * Readonly attribute width indicates the proposed width of this attribute
   */
  get readonlyWidth(): $Keys<typeof ATTRIBUTE_WIDTH> {
    return ATTRIBUTE_WIDTH.MEDIUM;
  }

  /**
   */
  get layoutHintRules(): LayoutHintRuleCollection {
    return this._layoutHintRules;
  }

  /**
   */
  set layoutHintRules(rules: LayoutHintRuleCollection) {
    this._layoutHintRules = rules;
  }

  /**
   */
  processLayoutHintRules(attributes: AttributeCollection): void {
    if (this._layoutHintRules) {
      this.layoutHintRules.process(this, attributes);
    }
  }

  /**
   * Indicates if content comes from the data service
   */
  get hasContentFromData(): boolean {
    return hasAllContentInData();
  }

  /**
   */
  get content(): AttributeContent {
    return new AttributeContent(this.data.content);
  }
}
